import React, { useEffect, useCallback, useState } from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { UserService } from 'gw-capability-gateway';
import { MLStackTraceBoxComponent } from 'ml-components-common-react';
import { MLTagManagerService } from 'ml-portals-services-react'
import { MLReadableErrorUtil } from 'ml-portals-util-react';

import metadata from './MLEndorsementContactUsPage.metadata.json5';
import styles from './MLEndorsementContactUsPage.module.scss';
import messages from './MLEndorsementContactUsPage.messages';

const ENTITY_VALIDATION_ERROR_CODE = '608';
const RATING_ERROR_CODE = '806';
const GEOSPATIAL_RATING_ERROR_CODE = '807';
const DOWN_PAYMENT_ERROR_MSG = 'Payment must be received prior to issuing policy';
const GW_ERROR_CODE = 'GW_ERROR';

function MLEndorsementContactUsPage(props) {
    const { 
        history,
        location 
    } = props;
    const { errorCodes, lobEndorsementURL, endorsementProducts, portalCode } = appConfig;
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { replaceUnreadableError } = MLReadableErrorUtil();
    const policyNumber = _.get(location, 'state.gwInfo.params[0].policyNumber');
    const productCode = _.get(location, 'state.gwInfo.params[0].baseData.productCode');
    const isAgent = portalCode === 'GPA';

    const [stateContactUsMsg, setStateContactUsMsg] = useState(
        translator(messages.mlDefaultContactUs, {
            strong: (chunks) => <strong>{ chunks }</strong>,
            br: () => <br />
        }));
    const [stateErrorMsgs, setStateErrorMsgs] = useState([]);
    const [stateDisplayableErrorMsgs, setStateDisplayableErrorMsgs] = useState([]);
    const [stateHasValidationErrors, setStateHasValidationErrors] = useState(false);
    const [stateAreValidationErrorsFixable, setStateAreValidationErrorsFixable] = useState(false);
    const [appErrors, setAppErrors] = useState([]);
    const [stackTrace, setStackTrace] = useState('');
    const [currentUser, setCurrentUser] = useState({});
    const isIA = _.get(currentUser, 'isIA');
    const isPCS = _.get(currentUser, 'isPCS');
    const isMetGA = _.get(currentUser, 'isMetGA');

    
    const parseParamsFromBaseErrorString = useCallback((baseErrorString) => {
        let uwIssues = [];

        let messageString;
        let stackTraceString;
        let formattedStackTraceString;
        let displayableErrorMessages = [];

        // Split strings into before and after stack trace
        const indexOfStackTrace = baseErrorString ? baseErrorString.indexOf('StackTrace_ML') : -1;
        const indexOfMessages = baseErrorString ? baseErrorString.indexOf('Messages_ML') : -1;
        if (indexOfStackTrace !== -1) {
            stackTraceString = baseErrorString.substring(indexOfStackTrace);
        }
        
        if (indexOfMessages !== -1) {
            messageString = baseErrorString.substring(0, indexOfMessages);
        } else if (indexOfStackTrace !== -1) {
            messageString = baseErrorString.substring(0, indexOfStackTrace);
        } else {
            messageString = baseErrorString || '';
        }

        const errorMessageStringArray = messageString.split(' ');
        const outerErrorCodeIndex = messageString.indexOf('ErrorCode:');
        const outerErrorCodeValue = outerErrorCodeIndex !== -1 ? errorMessageStringArray[outerErrorCodeIndex + 1] : undefined;
        const outerErrorCodeNumericValue = !_.isUndefined(outerErrorCodeValue) ? parseInt(outerErrorCodeValue, 10) : undefined;

        const innerErrorCodeIndex = errorMessageStringArray.lastIndexOf('ErrorCode:');
        const innerErrorCodeValue = innerErrorCodeIndex !== -1 ? errorMessageStringArray[innerErrorCodeIndex + 1] : undefined;
        const innerErrorCodeNumericValue = !_.isUndefined(innerErrorCodeValue) ? parseInt(innerErrorCodeValue, 10) : undefined


        // Get UW Issues
        if (outerErrorCodeNumericValue === errorCodes.APPLICATION_ERROR_ML && messageString.indexOf('|') !== -1) {
            const errorMsgsByNewLine = messageString.split('|');
            const uwIssuesArrayNonFormatted = errorMsgsByNewLine.splice(1);
            uwIssues = uwIssuesArrayNonFormatted.map((uwIssueStr) => {
                // UWIssues in format of code_ML:{code};LongDescription:{desc};|
                if (uwIssueStr.includes('Code_ML') && uwIssueStr.includes('LongDescription')) {
                    const uwIssuesStringArray = uwIssueStr.split(';');
                    return {
                        code_ML: uwIssuesStringArray[1],
                        longDescription: uwIssuesStringArray[3]
                    };
                }
            })
        } else {
            const indexOfMessageSplit = messageString.indexOf('|');
            if (indexOfMessageSplit !== -1) {
                const fullErrorMessageString = messageString.substring(0, indexOfMessageSplit);
                const errorMessageSplit = fullErrorMessageString.split('ErrorMessage:');
                if (errorMessageSplit.length > 0) {
                    const readableErrorMessages = errorMessageSplit.map((message) => replaceUnreadableError(message));
                    displayableErrorMessages = [readableErrorMessages[1]];
                }
            } else {
                try {
                    const errorExceptionML = messageString.split('ErrorMessage:')
                        .filter((msg) => !msg.includes("ErrorCode:"))
                        .map((msg) => {
                            const idx = msg.indexOf("ExceptionML:");
                            if (idx > -1) {
                                return msg.substring(idx).replace("ExceptionML:", "").trim();
                            }
                            return msg;
                        });

                    const getBaseErrorMessage = baseErrorString.substring(indexOfMessages);
                    const indexOfMessages_ML = getBaseErrorMessage.indexOf('Messages_ML');
                    const filteredErrorExceptionMessages = getBaseErrorMessage.substring(indexOfMessages_ML).replace("Messages_ML:", "")
                        .trim().slice(1, -1).split(', ');
                    const isAllErrorsSame = (filtereMessages) => filtereMessages.every((value) => value === filtereMessages[0]);

                    if (isAllErrorsSame(filteredErrorExceptionMessages) && errorExceptionML[0].includes(filteredErrorExceptionMessages[0])) {
                        displayableErrorMessages.push(filteredErrorExceptionMessages[0]);
                    } else {
                        displayableErrorMessages.push(errorExceptionML);
                    }
                } catch (e) {
                    console.error(e);
                    displayableErrorMessages = [];
                }
            }
        }

        if (stackTraceString) {
            const startIndexOfStackTrace = stackTraceString.indexOf(':');
            formattedStackTraceString = stackTraceString.substring(startIndexOfStackTrace + 1);
        }

        return {
            outerErrorCode: outerErrorCodeNumericValue,
            innerErrorCode: innerErrorCodeNumericValue,
            uwIssues,
            formattedStackTraceString,
            displayableErrorMessages
        }
    }, [errorCodes, replaceUnreadableError]);

    const isRatingError = (errorCode) => {
        return errorCode === RATING_ERROR_CODE || errorCode === GEOSPATIAL_RATING_ERROR_CODE;
    };

    const getErrorMsgsForIAPCSMetGA = useCallback((error, errorCode, hasValidationErrors, areValidationErrorsFixable, retryable) => {
        function translate(message, args = {}){
            return (
                <span>
                    {translator(message, {...args,
                        strong: (chunks) => <strong>{chunks}</strong>
                    })}
                </span>
            );
        }

        const errorMsgs = [];
        if (hasValidationErrors) {
            errorMsgs.push(translate(messages.IAPCSValidation))
            const message1 = isPCS ? translate(messages.EAPCSValidationContactUs) : translate(messages.IAPCSValidationContactUs);
            if(policyNumber && errorCode) {
                const message2 = translate(messages.IAPCSValidationContactUs2, {
                    policyNumber,
                    errorCode
                });
                errorMsgs.push(<>{message1} {message2}</>);
            }else{
                errorMsgs.push(message1);
            }
        } else if (retryable) {
            errorMsgs.push(translate(messages.IAPCSRetryable))
            const message1 = isPCS ? translate(messages.EAPCSRetryableContactUs) : translate(messages.IAPCSRetryableContactUs);
            if(policyNumber && errorCode) {
                const message2 = translate(messages.IAPCSRetryableContactUs2, {
                    policyNumber,
                    errorCode
                });
                errorMsgs.push(<>{message1} {message2}</>);
            }else{
                errorMsgs.push(message1);
            }
        } else if(isRatingError(errorCode)) {
            isPCS ? errorMsgs.push(translate(messages.EAPCSNonRetryable)) : errorMsgs.push(translate(messages.IAPCSNonRetryable));
            if(policyNumber && errorCode) {
                const message1 = translate(messages.IAPCSNonRetryableContactUsWithoutErrorCode, {
                    policyNumber,
                    errorCode
                });
                errorMsgs.push(message1);
            }
        } else {
            isPCS ? errorMsgs.push(translate(messages.EAPCSNonRetryable)) : errorMsgs.push(translate(messages.IAPCSNonRetryable));
            if(policyNumber && errorCode) {
                const message1 = translate(messages.IAPCSNonRetryableContactUs, {
                    policyNumber,
                    errorCode
                });
                errorMsgs.push(message1);
            }
        }
        return errorMsgs;
    }, [policyNumber, translator, isPCS]);
    const getErrorMsgsForGCS = useCallback((error, errorCode, hasValidationErrors, areValidationErrorsFixable, retryable) => {
        function translate(message, args = {}) {
            return (
                <span>
                    {translator(message, {...args,
                        strong: (chunks) => <strong>{chunks}</strong>
                    })}
                </span>
            );
        }

        const errorMsgs = [];
        if (hasValidationErrors) {
            errorMsgs.push(translate(messages.GCSValidation))
        } else if (retryable) {
            const message1 = translate(messages.CSCRetryableContactUs, {
                errorCode: errorCode ? <>due to error code <strong>{errorCode}</strong></> : ''
            });
            errorMsgs.push(message1);
        } else if(isRatingError(errorCode)) {
            errorMsgs.push(translate(messages.CSCNonRetryableContactUsWithoutErrorCode))
        } else {
            const message1 = translate(messages.CSCNonRetryableContactUs, {
                errorCode: errorCode ? <>due to error code <strong>{errorCode}</strong></> : ''
            });
            errorMsgs.push(message1);
        }
        return errorMsgs;
    }, [translator]);

    const getErrorMsgs = useCallback((error, errorCode, hasValidationErrors, areValidationErrorsFixable, retryable) => {
        let errorMessages;
        if (isPCS || isIA || isMetGA) {
            errorMessages = getErrorMsgsForIAPCSMetGA(error, errorCode, hasValidationErrors, areValidationErrorsFixable, retryable);
        } else {
            errorMessages = getErrorMsgsForGCS(error, errorCode, hasValidationErrors, areValidationErrorsFixable, retryable);
        }
        return errorMessages;
    }, [getErrorMsgsForGCS, getErrorMsgsForIAPCSMetGA, isIA, isMetGA, isPCS]);

    const hasFixableValidationErrorsOnly = useCallback((errorCode, errorMessages) => {
        if (errorCode === ENTITY_VALIDATION_ERROR_CODE && errorMessages.length > 0) {
            return !errorMessages.find((err) => err.indexOf(DOWN_PAYMENT_ERROR_MSG) > -1);
        }
        return false;
    }, []);


    const setDefaultErrorStateFields = useCallback(() => {
        function translate(message, args = {}){
            return (
                <span>
                    {translator(message, {...args,
                        strong: (chunks) => <strong>{chunks}</strong>
                    })}
                </span>
            );
        }
        const message1 = translate(messages.CSCNonRetryableContactUs, {
            errorCode: ''
        });
        const errorMessages = [message1];
        setStateErrorMsgs(errorMessages);
    }, [translator]);

    const handleUWError = useCallback((errorMessages, errorCode, uwIssues) => {
        errorMessages = [translator(messages.mlUwIssueUnableToContinue)];

        if (errorCode === errorCodes.BLOCK_QUOTE_UNDERWRITING_ERROR) {
            setStateContactUsMsg(translator(messages.mlContactUsChatPolicyNum, {
                policyNumber,
                strong: (chunks) => <strong>{ chunks }</strong>
            }));
            setAppErrors(uwIssues);
        } 
        else if (errorCode === errorCodes.BLOCK_QUOTE_UNDERWRITING_ERROR_WITH_REFERRAL) {
            setAppErrors(uwIssues);
        } else {
            errorMessages = [
                <>
                    {translator(messages.mlDefaultErrorMsg, {
                        br: () => <br />,
                    })}
                </>
            ];
        }
        setStateErrorMsgs(errorMessages);
    }, [errorCodes, policyNumber, translator]);

    const setErrorMessages = useCallback((state) => {
        let errorCode = null;
        let errorMessages = [];

        try {
            if (state.gwInfo) {
                const error = state;
                const { innerErrorCode, uwIssues, formattedStackTraceString, displayableErrorMessages } = parseParamsFromBaseErrorString(error.baseError);
                errorCode = state.errorCode === GW_ERROR_CODE ? innerErrorCode : state.errorCode;

                if (!_.isEmpty(uwIssues) && !isAgent) {
                    handleUWError(errorMessages, errorCode, uwIssues);
                } else {
                    const hasValidationErrors = errorCode === ENTITY_VALIDATION_ERROR_CODE || displayableErrorMessages.length > 0;
                    const areValidationErrorsFixable = hasFixableValidationErrorsOnly(errorCode, displayableErrorMessages);
                    errorMessages = getErrorMsgs(error, errorCode, hasValidationErrors, areValidationErrorsFixable);
                    setStateErrorMsgs(errorMessages);
                    setStateDisplayableErrorMsgs(displayableErrorMessages);
                    setStateHasValidationErrors(hasValidationErrors);
                    setStateAreValidationErrorsFixable(areValidationErrorsFixable);
                }


                if (formattedStackTraceString) {
                    setStackTrace(formattedStackTraceString);
                }
            } else if (state.customError) {
                const error = state.customError;
                const appErrorMessages = error.appErrorMessages;
                if (!_.isEmpty(appErrorMessages)) {
                    setStateErrorMsgs(appErrorMessages);
                }
            } else {
                setDefaultErrorStateFields();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            setDefaultErrorStateFields();
        }
    }, [getErrorMsgs, handleUWError, hasFixableValidationErrorsOnly, isAgent, parseParamsFromBaseErrorString, setDefaultErrorStateFields]);

    const setJSErrorMessages = useCallback(() => {
        if(location.state?.stack){
            console.error(location.state);
            setStackTrace(location.state.stack)
        }
    }, [location.state])

    useEffect(() => {
        if (!isAgent) {
            return;
        }
        UserService.getCurrentUser(authHeader).then((user) => {
            setCurrentUser(user);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        MLTagManagerService.sendEvent('formError', {form: 'unable to complete'});
        if(currentUser){
            if(!location.state?.baseError && location.state?.stack){
                // JS Error
                setJSErrorMessages(location.state);
                setDefaultErrorStateFields()
            }else{
                setErrorMessages(location.state);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser])

    const onReturnToPolicyDetailsClick = () => {
        history.push(`/policies/${policyNumber}/summary`);
    }
    
    const startPolicyChange = useCallback((chosenStepPathML) => {
        // $scope.sendTagManagerEvent(label);
        if (endorsementProducts.includes(productCode)) {
            history.push(lobEndorsementURL[productCode], { policyNumber, chosenStepPathML });
        }
    }, [endorsementProducts, history, lobEndorsementURL, policyNumber, productCode]);

    const onReturnToPolicyChangeClick = () => {
        // EndorsementReferralService.loadAndJump('endorsementAdditionalChanges', $scope.policyNumber);
        startPolicyChange('endorsementAdditionalChanges');
    }

    const overrideProps = {
        appErrorIterable: {
            visible: !isAgent,
            data: appErrors
        },
        contactUsMsg: {
            visible: !isAgent && !_.isEmpty(stateContactUsMsg),
            content: (<>
                { stateContactUsMsg }
            </>) 
        },
        unableToCompleteQuoteTitle: {
            visible: stateHasValidationErrors && stateAreValidationErrorsFixable
        },
        unableToCompleteQuoteOnlineTitle: {
            visible: !(stateHasValidationErrors && stateAreValidationErrorsFixable)
        },
        errorMessagesSection: {
            visible: stateErrorMsgs.length > 0
        },
        errorMessage: {
            data: stateErrorMsgs && stateErrorMsgs.map((val) => {return {message: val}})
        },
        displayableErrorMessagesSection: {
            visible: stateDisplayableErrorMsgs.length > 0
        },
        reasonsDiv: {
            visible: stateErrorMsgs.length > 0
        },
        displayableErrorMessage: {
            data: stateDisplayableErrorMsgs.map((val) => {return {message: val}})
        },
        returnToQuoteLink: {
            visible: !!policyNumber && !!stateHasValidationErrors
        },
        returnToAccountLink: {
            visible: !!policyNumber && !stateHasValidationErrors
        },
        mlstacktracebox: {
            visible: !_.isEmpty(stackTrace),
            stackTrace
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onReturnToPolicyDetailsClick,
            onReturnToPolicyChangeClick
        },
        resolveComponentMap: {
            mlstacktraceboxcomponent: MLStackTraceBoxComponent
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

export default withRouter(MLEndorsementContactUsPage);
